import cn from 'classnames'
import {ErrorMessage, useField} from 'formik'
import {FC, useCallback} from 'react'
import {Accept, DropzoneOptions, useDropzone} from 'react-dropzone'

import {useAdminFilePaste} from '@/components/application-draft/hooks/useAdminFilePaste'
import {useFileUploadContent} from '@/content/fileUpload'
import {Trans} from '@/i18n'
import {FORM_DROPZONE_HEIGHT} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {maxFileSize} from '@/utils/constants'
import {getMaxFileSizeInMB, useRejectedFileErrorMessage} from '@/utils/document/helpers'

import {Text, TextFontWeight, TextVariant} from '../content/Text'
import {FormErrorMessage} from '../form/FormErrorMessage'
import {UploadedFilePreview} from './UploadedFilePreview'

const maxFiles = 1

type FormikDropzoneProps = {
  name: string
  maxSize?: number
  accept?: Accept
  supportedFormatsText: string
  currentFilesCount?: number
  isAdminFilePasteEnabled?: boolean
}

export const FormikDropzone: FC<React.PropsWithChildren<FormikDropzoneProps>> = ({
  name,
  maxSize = maxFileSize,
  accept,
  supportedFormatsText,
  currentFilesCount,
  isAdminFilePasteEnabled = false,
}) => {
  const {dropzoneDescripton} = useFileUploadContent()
  const [field, , {setValue, setTouched}] = useField(name)

  const isFileUploaded = !!field.value
  const maxFileSizeInMB = getMaxFileSizeInMB(maxSize)

  const onDrop: DropzoneOptions['onDrop'] = useCallback(
    (acceptedFiles: File[]) => {
      setValue(acceptedFiles[0])
      setTouched(true)
    },
    [setValue, setTouched],
  )

  const {getRootProps, getInputProps, isDragActive, fileRejections} = useDropzone({
    onDrop,
    minSize: 1,
    maxSize: maxSize,
    accept,
    maxFiles,
    multiple: maxFiles !== 1,
  })

  // Warning: It is expected that only one instance of FormikDropzone (which has useAdminFilePaste with isEnabled = true) will be mounted
  useAdminFilePaste({
    maxFiles,
    currentFilesCount: currentFilesCount ?? 0,
    isEnabled: isAdminFilePasteEnabled,
    onDropAccepted: onDrop,
  })

  const rejectedErrorMessage = useRejectedFileErrorMessage(fileRejections, maxSize, maxFiles)

  return (
    <>
      {isFileUploaded ? (
        <UploadedFilePreview file={field.value} removeFile={() => setValue(null)} />
      ) : (
        <>
          <label
            {...getRootProps()}
            htmlFor="file"
            className={cn('dropzone', isDragActive && 'active')}
          >
            <input {...getInputProps()} multiple={false} data-test="fileInput" />
            <div>
              <Text variant={TextVariant.text14} color={theme.palette.text.light} align="center">
                <Trans i18nKey={dropzoneDescripton}>
                  text
                  <Text
                    variant={TextVariant.text14}
                    element="span"
                    color={theme.palette.text.primary}
                    fontWeight={TextFontWeight.bold}
                  >
                    text
                  </Text>
                  text
                </Trans>
              </Text>
              <div className="description">
                <Text
                  variant={TextVariant.caption}
                  element="p"
                  color={theme.palette.text.light}
                  align="center"
                >
                  {supportedFormatsText}
                </Text>
              </div>
            </div>
          </label>
          {rejectedErrorMessage ? (
            <FormErrorMessage
              message={
                <Trans
                  i18nKey={rejectedErrorMessage}
                  values={{
                    maxFiles,
                    maxFileSize: maxFileSizeInMB,
                    supportedFormats: supportedFormatsText,
                  }}
                />
              }
            />
          ) : (
            <ErrorMessage name="file" render={(msg) => <FormErrorMessage message={msg} />} />
          )}
        </>
      )}

      <style jsx>{`
        .dropzone {
          display: flex;
          justify-content: center;
          align-items: center;
          height: ${FORM_DROPZONE_HEIGHT};
          padding: ${theme.spacing[3]} ${theme.spacing[4]};
          border: 2px dashed ${theme.palette.gray[200]};
          border-radius: 12px;
          cursor: pointer;
        }
        .filled {
          border: 1px solid ${theme.palette.gray[200]};
          cursor: default;
        }
        .active {
          background-color: ${theme.palette.gray[50]};
          border: 2px solid ${theme.palette.gray[300]};
        }
        .description {
          margin-top: ${theme.spacing[1]};
        }
      `}</style>
    </>
  )
}
